import { Controller } from "@hotwired/stimulus"
import confetti from "canvas-confetti"

export default class extends Controller {
  static targets = [
    "step",
    "progress",
    "progressBar",
    "progressContainer",
    "stepIndicator",
    "protagonistInput",
    "incidentInput",
    "objectiveInput",
    "antagonistInput",
    "genreInput",
    "projectTypeInput",
    "titleInput",
    "emailInput",
    "emailError",
    "submitButton",
    "submitText",
    "loadingSpinner",
    "prevButton",
    "nextButton",
    "loglinePreview",
    "modal",
    "navigation",
    "header",
    "headerTitle",
    "headerSubtitle",
    "finalLogline",
    "continueLink",
    "genreOption",
    "wizardContent",
    "typeCheck",
    "genreCheck",
    "selectedGenresDisplay",
    "selectedGenresText",
    "gmailSection",
    "userEmailDisplay",
    "resendButton",
    "heroGrid",
    "marketingColumn",
    "conceptEditSection",
    "conceptTextarea",
    "saveConceptButton",
    "conceptSavedMessage",
    "examplesPopover",
    "wizardWrapper",
    "ctaBadge",
    "wizardCard",
    "polishButton",
    "polishButtonText"
  ]

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 7 },
    inputSteps: { type: Number, default: 6 },
    validateEmailUrl: String,
    submitUrl: String,
    polishUrl: String,
    redirectUrl: String
  }

  connect() {
    // Check if wizard was already completed in this session
    if (this.isWizardCompleted()) {
      this.showCompletedState()
      return
    }

    // Initialize selected genres array
    this.selectedGenres = []

    // Store the edited concept text (null means not yet edited)
    this.editedConceptText = null

    // Track initial load to avoid auto-scrolling
    this.isInitialLoad = true
    this.showStep(this.currentStepValue)
    this.isInitialLoad = false
    this.updateProgress()

    // Show type checkmark by default (screenplay is pre-selected)
    this.showTypeCheck()
  }

  isWizardCompleted() {
    return localStorage.getItem("wizardCompleted") === "true"
  }

  markWizardCompleted() {
    localStorage.setItem("wizardCompleted", "true")
  }

  showCompletedState() {
    // Show a message that they've already completed the wizard
    this.stepTargets.forEach(step => step.classList.add("hidden"))

    // Hide navigation
    if (this.hasNavigationTarget) {
      this.navigationTarget.classList.add("hidden")
    }

    // Hide progress
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add("hidden")
    }

    // Update header
    if (this.hasHeaderTitleTarget) {
      this.headerTitleTarget.textContent = "Already Submitted"
    }
    if (this.hasHeaderSubtitleTarget) {
      this.headerSubtitleTarget.textContent = "Check your email for your outline"
    }

    // Show the success step content area with a different message
    const contentArea = this.element.querySelector(".p-8")
    if (contentArea) {
      contentArea.innerHTML = `
        <div class="text-center py-8">
          <div class="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <svg class="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">You've already submitted your concept!</h3>
          <p class="text-gray-600 mb-6">Check your email for your PDF outline and AI feedback.</p>
          <a href="/users/sign_in" class="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition">
            Sign In to Continue
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
            </svg>
          </a>
        </div>
      `
    }
  }

  openModal(event) {
    event?.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
    }
  }

  closeModal(event) {
    event?.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) {
      this.closeModal(event)
    }
  }

  // Project Type Selection (graphical cards)
  selectType(event) {
    event.preventDefault()
    const selectedType = event.currentTarget.dataset.type

    // Update hidden input
    if (this.hasProjectTypeInputTarget) {
      this.projectTypeInputTarget.value = selectedType
    }

    // Update visual state of all type cards
    const typeCards = this.element.querySelectorAll(".type-card")
    typeCards.forEach(card => {
      const cardType = card.dataset.type
      const isSelected = cardType === selectedType
      const checkIcon = card.querySelector(".check-icon")

      if (isSelected) {
        card.classList.add("border-primary-500", "bg-primary-50", "ring-2", "ring-primary-500")
        card.classList.remove("border-gray-200", "hover:border-gray-300", "hover:bg-gray-50")
        if (checkIcon) checkIcon.classList.remove("hidden")
      } else {
        card.classList.remove("border-primary-500", "bg-primary-50", "ring-2", "ring-primary-500")
        card.classList.add("border-gray-200", "hover:border-gray-300", "hover:bg-gray-50")
        if (checkIcon) checkIcon.classList.add("hidden")
      }
    })

    // Show type checkmark
    this.showTypeCheck()
  }

  showTypeCheck() {
    if (this.hasTypeCheckTarget) {
      this.typeCheckTarget.classList.remove("hidden")
    }
  }

  showGenreCheck() {
    if (this.hasGenreCheckTarget) {
      this.genreCheckTarget.classList.remove("hidden")
    }
  }

  hideGenreCheck() {
    if (this.hasGenreCheckTarget) {
      this.genreCheckTarget.classList.add("hidden")
    }
  }

  // Inline genre selection
  selectGenreInline(event) {
    event.preventDefault()
    const selectedGenre = event.currentTarget.dataset.genre

    // Toggle genre selection (max 2)
    const index = this.selectedGenres.indexOf(selectedGenre)
    if (index > -1) {
      // Already selected, remove it
      this.selectedGenres.splice(index, 1)
    } else if (this.selectedGenres.length < 2) {
      // Not selected and we have room, add it
      this.selectedGenres.push(selectedGenre)
    } else {
      // Already have 2, don't add more
      return
    }

    // Update visual state of all genre options
    this.updateGenreOptionVisuals()

    // Update hidden input
    if (this.hasGenreInputTarget) {
      this.genreInputTarget.value = this.selectedGenres.join(",")
    }

    // Show/hide genre checkmark based on selection
    if (this.selectedGenres.length > 0) {
      this.showGenreCheck()
    } else {
      this.hideGenreCheck()
    }
  }

  updateGenreOptionVisuals() {
    const genreLabels = []

    this.genreOptionTargets.forEach(option => {
      const optionGenre = option.dataset.genre
      const isSelected = this.selectedGenres.includes(optionGenre)
      const checkIcon = option.querySelector(".genre-check")

      if (isSelected) {
        // Darken background and show checkmark
        option.classList.add("ring-2", "ring-lime-500", "shadow-inner")
        option.style.filter = "brightness(0.85) saturate(1.3)"
        if (checkIcon) checkIcon.classList.remove("hidden")
        // Collect label for display
        const label = option.querySelector("span")?.textContent
        if (label) genreLabels.push(label)
      } else {
        option.classList.remove("ring-2", "ring-lime-500", "shadow-inner")
        option.style.filter = ""
        if (checkIcon) checkIcon.classList.add("hidden")
      }
    })

    // Update selected genres display
    if (this.hasSelectedGenresDisplayTarget && this.hasSelectedGenresTextTarget) {
      if (genreLabels.length > 0) {
        this.selectedGenresDisplayTarget.classList.remove("hidden")
        this.selectedGenresTextTarget.textContent = genreLabels.join(", ")
      } else {
        this.selectedGenresDisplayTarget.classList.add("hidden")
      }
    }
  }

  nextStep(event) {
    event?.preventDefault()

    if (!this.validateCurrentStep()) return

    const previousStep = this.currentStepValue

    if (this.currentStepValue < this.totalStepsValue) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)
      this.updateProgress()

      // Fire confetti after genre/type selection (moving from step 5 to step 6)
      if (previousStep === 5 && this.currentStepValue === 6) {
        this.fireConfetti()
      }
    }
  }

  prevStep(event) {
    event?.preventDefault()

    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.showStep(this.currentStepValue)
      this.updateProgress()
    }
  }

  showStep(stepNumber) {
    const isSuccessStep = stepNumber === this.totalStepsValue
    const isEmailStep = stepNumber === this.inputStepsValue
    const isDetailsStep = stepNumber === 5

    this.stepTargets.forEach((step, index) => {
      const isActive = index + 1 === stepNumber
      step.classList.toggle("hidden", !isActive)
    })

    // Hide navigation on success step
    if (this.hasNavigationTarget) {
      this.navigationTarget.classList.toggle("hidden", isSuccessStep)
    }

    // Hide progress bar on email step and success step
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.toggle("hidden", isEmailStep || isSuccessStep)
    }

    // Update header based on step
    if (isSuccessStep) {
      if (this.hasHeaderTitleTarget) {
        this.headerTitleTarget.textContent = "Success!"
      }
      if (this.hasHeaderSubtitleTarget) {
        this.headerSubtitleTarget.textContent = "Your story awaits"
      }
    } else if (isEmailStep) {
      if (this.hasHeaderTitleTarget) {
        this.headerTitleTarget.textContent = "Great Work!"
      }
      if (this.hasHeaderSubtitleTarget) {
        this.headerSubtitleTarget.textContent = "Get your free notes and outline template!"
      }
    } else if (isDetailsStep) {
      if (this.hasHeaderTitleTarget) {
        this.headerTitleTarget.textContent = "A Few Last Details"
      }
      if (this.hasHeaderSubtitleTarget) {
        this.headerSubtitleTarget.textContent = "Tell us about your project"
      }
    } else {
      // Reset to default for other steps
      if (this.hasHeaderTitleTarget) {
        this.headerTitleTarget.textContent = "Build Your Concept"
      }
      if (this.hasHeaderSubtitleTarget) {
        this.headerSubtitleTarget.textContent = "Answer a few questions and get free notes on your story!"
      }
    }

    // Update prev button visibility (hide on step 1 and success)
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("hidden", stepNumber === 1 || isSuccessStep)
    }

    // Update next/submit button
    if (this.hasNextButtonTarget) {
      // Hide next button on email step (show submit instead) and success step
      this.nextButtonTarget.classList.toggle("hidden", isEmailStep || isSuccessStep)
    }

    if (this.hasSubmitButtonTarget) {
      // Show submit button only on email step
      this.submitButtonTarget.classList.toggle("hidden", !isEmailStep)
    }

    // Update concept preview on later steps
    if (stepNumber >= 4) {
      this.updateConceptPreview()
    }

    // Layout control for steps 5, 6, 7
    this.updateLayout(stepNumber)

    // Focus the input on the current step (not on success, not on initial load)
    if (!isSuccessStep && !this.isInitialLoad) {
      setTimeout(() => {
        const input = this.getCurrentInput()
        if (input) input.focus()
      }, 100)
    }
  }

  // Controls the hero grid layout based on current step
  updateLayout(stepNumber) {
    const isDetailsStep = stepNumber === 5
    const isEmailStep = stepNumber === this.inputStepsValue
    const isSuccessStep = stepNumber === this.totalStepsValue
    const needsFullWidth = isEmailStep || isSuccessStep

    // Step 5: Show concept edit section, hide marketing column
    if (isDetailsStep) {
      // Hide marketing, show concept edit
      if (this.hasMarketingColumnTarget) {
        this.marketingColumnTarget.classList.add("hidden")
      }
      if (this.hasConceptEditSectionTarget) {
        this.conceptEditSectionTarget.classList.remove("hidden")
        // Populate textarea with current concept text
        if (this.hasConceptTextareaTarget) {
          this.conceptTextareaTarget.value = this.editedConceptText || this.buildConceptText()
        }
      }
      // Restore two-column grid
      if (this.hasHeroGridTarget) {
        this.heroGridTarget.classList.remove("lg:grid-cols-1", "max-w-2xl", "mx-auto")
        this.heroGridTarget.classList.add("lg:grid-cols-2")
      }
    } else if (needsFullWidth) {
      // Steps 6 & 7: Full width centered - hide both left columns
      if (this.hasMarketingColumnTarget) {
        this.marketingColumnTarget.classList.add("hidden")
      }
      if (this.hasConceptEditSectionTarget) {
        this.conceptEditSectionTarget.classList.add("hidden")
      }
      // Switch to single column centered
      if (this.hasHeroGridTarget) {
        this.heroGridTarget.classList.remove("lg:grid-cols-2")
        this.heroGridTarget.classList.add("lg:grid-cols-1", "max-w-2xl", "mx-auto")
      }
      // Hide CTA badge on email/success steps
      if (this.hasCtaBadgeTarget) {
        this.ctaBadgeTarget.classList.add("hidden")
      }
    } else {
      // Steps 1-4: Show marketing column, hide concept edit
      if (this.hasMarketingColumnTarget) {
        this.marketingColumnTarget.classList.remove("hidden")
      }
      if (this.hasConceptEditSectionTarget) {
        this.conceptEditSectionTarget.classList.add("hidden")
      }
      // Restore two-column grid
      if (this.hasHeroGridTarget) {
        this.heroGridTarget.classList.remove("lg:grid-cols-1", "max-w-2xl", "mx-auto")
        this.heroGridTarget.classList.add("lg:grid-cols-2")
      }
      // Show CTA badge
      if (this.hasCtaBadgeTarget) {
        this.ctaBadgeTarget.classList.remove("hidden")
      }
    }
  }

  // Toggle examples popover (one per step)
  toggleExamples(event) {
    event?.preventDefault()
    // Find the popover sibling of the clicked button
    const popover = event.currentTarget.parentElement.querySelector('[data-homepage-wizard-target="examplesPopover"]')
    if (!popover) return

    const isHidden = popover.classList.contains("hidden")

    // Close all popovers first
    this.examplesPopoverTargets.forEach(p => p.classList.add("hidden"))

    // Toggle the clicked one
    if (isHidden) {
      popover.classList.remove("hidden")
    }
  }

  // Save concept adjustments from the editable textarea
  saveConceptAdjustments(event) {
    event?.preventDefault()
    if (this.hasConceptTextareaTarget) {
      this.editedConceptText = this.conceptTextareaTarget.value.trim()
    }
    // Show "Saved!" feedback
    if (this.hasConceptSavedMessageTarget) {
      this.conceptSavedMessageTarget.classList.remove("hidden")
      setTimeout(() => {
        this.conceptSavedMessageTarget.classList.add("hidden")
      }, 2000)
    }
  }

  // Polish concept with AI
  async polishConcept(event) {
    event?.preventDefault()

    // Read from textarea if available (Step 5), otherwise use buildConceptText
    let currentConcept
    if (this.hasConceptTextareaTarget && this.conceptTextareaTarget.value.trim()) {
      currentConcept = this.conceptTextareaTarget.value.trim()
    } else {
      currentConcept = this.buildConceptText()
    }

    if (!currentConcept || currentConcept === "Your concept will appear here...") {
      return
    }

    // Show loading state
    if (this.hasPolishButtonTarget) {
      this.polishButtonTarget.disabled = true
    }
    if (this.hasPolishButtonTextTarget) {
      this.polishButtonTextTarget.textContent = "Polishing..."
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.polishUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          logline: currentConcept,
          protagonist: this.hasProtagonistInputTarget ? this.protagonistInputTarget.value.trim() : "",
          incident: this.hasIncidentInputTarget ? this.incidentInputTarget.value.trim() : "",
          objective: this.hasObjectiveInputTarget ? this.objectiveInputTarget.value.trim() : "",
          antagonist: this.hasAntagonistInputTarget ? this.antagonistInputTarget.value.trim() : ""
        })
      })

      const data = await response.json()

      if (data.success && data.polished) {
        // Update the concept with the polished version
        this.editedConceptText = data.polished
        this.updateConceptPreview()

        // Update textarea if visible
        if (this.hasConceptTextareaTarget) {
          this.conceptTextareaTarget.value = data.polished
        }

        // Show success state briefly
        if (this.hasPolishButtonTextTarget) {
          this.polishButtonTextTarget.textContent = "Polished!"
          setTimeout(() => {
            this.polishButtonTextTarget.textContent = "Polish"
          }, 2000)
        }
      } else {
        // Show error
        if (this.hasPolishButtonTextTarget) {
          this.polishButtonTextTarget.textContent = "Try again"
          setTimeout(() => {
            this.polishButtonTextTarget.textContent = "Polish"
          }, 2000)
        }
      }
    } catch (error) {
      console.error("Polish error:", error)
      if (this.hasPolishButtonTextTarget) {
        this.polishButtonTextTarget.textContent = "Try again"
        setTimeout(() => {
          this.polishButtonTextTarget.textContent = "Polish"
        }, 2000)
      }
    } finally {
      if (this.hasPolishButtonTarget) {
        this.polishButtonTarget.disabled = false
      }
    }
  }

  getCurrentInput() {
    switch (this.currentStepValue) {
      case 1: return this.hasProtagonistInputTarget ? this.protagonistInputTarget : null
      case 2: return this.hasIncidentInputTarget ? this.incidentInputTarget : null
      case 3: return this.hasObjectiveInputTarget ? this.objectiveInputTarget : null
      case 4: return this.hasAntagonistInputTarget ? this.antagonistInputTarget : null
      case 5: return this.hasGenreInputTarget ? this.genreInputTarget : null
      case 6: return this.hasEmailInputTarget ? this.emailInputTarget : null
      default: return null
    }
  }

  updateProgress() {
    // Progress is based on input steps (1-6), not including success step (7)
    const progress = ((this.currentStepValue - 1) / (this.inputStepsValue - 1)) * 100

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${Math.min(progress, 100)}%`
    }

    // Update step indicators
    this.stepIndicatorTargets.forEach((indicator, index) => {
      const stepNum = index + 1
      indicator.classList.toggle("bg-primary-600", stepNum <= this.currentStepValue)
      indicator.classList.toggle("text-white", stepNum <= this.currentStepValue)
      indicator.classList.toggle("bg-gray-200", stepNum > this.currentStepValue)
      indicator.classList.toggle("text-gray-600", stepNum > this.currentStepValue)
    })
  }

  validateCurrentStep() {
    switch (this.currentStepValue) {
      case 1:
        return this.validateProtagonist()
      case 2:
        return this.validateIncident()
      case 3:
        return this.validateObjective()
      case 4:
        return this.validateAntagonist()
      case 5:
        return this.validateGenre()
      case 6:
        return true // Email validation happens on submit
      default:
        return true
    }
  }

  validateProtagonist() {
    if (this.hasProtagonistInputTarget) {
      const value = this.protagonistInputTarget.value.trim()
      if (!value) {
        this.showInputError(this.protagonistInputTarget, "Please describe your protagonist")
        return false
      }
      this.clearInputError(this.protagonistInputTarget)
    }
    return true
  }

  validateIncident() {
    if (this.hasIncidentInputTarget) {
      const value = this.incidentInputTarget.value.trim()
      if (!value) {
        this.showInputError(this.incidentInputTarget, "Please describe the knock")
        return false
      }
      this.clearInputError(this.incidentInputTarget)
    }
    return true
  }

  validateObjective() {
    if (this.hasObjectiveInputTarget) {
      const value = this.objectiveInputTarget.value.trim()
      if (!value) {
        this.showInputError(this.objectiveInputTarget, "Please describe what your protagonist must do")
        return false
      }
      this.clearInputError(this.objectiveInputTarget)
    }
    return true
  }

  validateAntagonist() {
    // Antagonist is optional but recommended
    return true
  }

  validateGenre() {
    // Genre is optional
    return true
  }

  showInputError(input, message) {
    input.classList.add("border-red-500", "ring-red-500")
    const errorEl = input.parentElement.querySelector(".error-message")
    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.remove("hidden")
    }
  }

  clearInputError(input) {
    input.classList.remove("border-red-500", "ring-red-500")
    const errorEl = input.parentElement.querySelector(".error-message")
    if (errorEl) {
      errorEl.classList.add("hidden")
    }
  }

  updateConceptPreview() {
    const concept = this.buildConceptText()

    // Update all logline preview targets (there may be multiple across steps)
    this.loglinePreviewTargets.forEach(target => {
      target.textContent = concept
    })
  }

  buildConceptText() {
    // If user has manually edited the concept, return that
    if (this.editedConceptText) {
      return this.editedConceptText
    }

    const protagonist = this.hasProtagonistInputTarget ? this.protagonistInputTarget.value.trim() : ""
    const incident = this.hasIncidentInputTarget ? this.incidentInputTarget.value.trim() : ""
    const objective = this.hasObjectiveInputTarget ? this.objectiveInputTarget.value.trim() : ""
    const antagonist = this.hasAntagonistInputTarget ? this.antagonistInputTarget.value.trim() : ""

    if (!protagonist && !incident && !objective) {
      return "Your concept will appear here..."
    }

    // Build concept in format: "When [protagonist] [incident], they must [objective] before [stakes]."
    let concept = ""

    // Clean up protagonist - remove leading articles for use after "When"
    let protagonistForIncident = protagonist.replace(/^(a|an|the)\s+/i, "")
    let protagonistCapitalized = protagonist.charAt(0).toUpperCase() + protagonist.slice(1)

    // Clean up incident - remove leading "when", "after", pronouns
    let incidentClean = incident
      .replace(/^(when|after)\s+/i, "")
      .replace(/^(he|she|they|it)\s+/i, "")

    // Clean up objective - remove leading "must", "has to", "needs to", and pronouns
    let objectiveClean = objective
      .replace(/^(must|has to|needs to|have to)\s+/i, "")
      .replace(/^(he|she|they|it)\s+(must|has to|needs to|have to)\s+/i, "")
      .replace(/^(he|she|they|it)\s+/i, "")

    // Clean up antagonist/stakes
    let stakesClean = antagonist
      .replace(/^(or|or else|before|against)\s+/i, "")

    // Build the concept
    if (incident && protagonist) {
      // Format: "When a struggling mother receives X, she must Y"
      concept = `When ${protagonistForIncident} ${incidentClean}`
    } else if (incident) {
      // No protagonist yet, just show incident
      concept = `When ${incidentClean}`
    } else if (protagonist) {
      // No incident, start with protagonist
      concept = protagonistCapitalized
    }

    // Add objective
    if (objective && objectiveClean) {
      if (incident && protagonist) {
        // After "When X, " we need a comma and "they must"
        concept += `, they must ${objectiveClean}`
      } else if (protagonist) {
        concept += ` must ${objectiveClean}`
      } else {
        concept += ` must ${objectiveClean}`
      }
    }

    // Add stakes/antagonist
    if (antagonist && stakesClean) {
      concept += ` before ${stakesClean}`
    }

    // Add period if not present
    if (concept && !concept.match(/[.!?]$/)) {
      concept += "."
    }

    return concept || "Your concept will appear here..."
  }

  async validateEmail() {
    if (!this.hasEmailInputTarget) return true

    const email = this.emailInputTarget.value.trim()

    if (!email) {
      this.showEmailError("Please enter your email address")
      return false
    }

    // Basic format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      this.showEmailError("Please enter a valid email address")
      return false
    }

    // Server-side validation
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.validateEmailUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({ email })
      })

      const data = await response.json()

      if (!data.valid) {
        this.showEmailError(data.error || "Please enter a valid email address")
        if (data.existing_user) {
          // Show login link
          this.showEmailError("This email is already registered. Please sign in instead.")
        }
        return false
      }

      this.clearEmailError()
      return true
    } catch (error) {
      console.error("Email validation error:", error)
      // Continue on network error - backend will validate
      this.clearEmailError()
      return true
    }
  }

  showEmailError(message) {
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.textContent = message
      this.emailErrorTarget.classList.remove("hidden")
    }
    if (this.hasEmailInputTarget) {
      this.emailInputTarget.classList.add("border-red-500", "ring-red-500")
    }
  }

  clearEmailError() {
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.classList.add("hidden")
    }
    if (this.hasEmailInputTarget) {
      this.emailInputTarget.classList.remove("border-red-500", "ring-red-500")
    }
  }

  async submit(event) {
    event.preventDefault()

    // Validate email first
    const emailValid = await this.validateEmail()
    if (!emailValid) return

    // Show loading state
    this.setLoading(true)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const formData = {
        email: this.hasEmailInputTarget ? this.emailInputTarget.value.trim() : "",
        protagonist: this.hasProtagonistInputTarget ? this.protagonistInputTarget.value.trim() : "",
        incident: this.hasIncidentInputTarget ? this.incidentInputTarget.value.trim() : "",
        objective: this.hasObjectiveInputTarget ? this.objectiveInputTarget.value.trim() : "",
        antagonist: this.hasAntagonistInputTarget ? this.antagonistInputTarget.value.trim() : "",
        genre: this.hasGenreInputTarget ? this.genreInputTarget.value : "",
        project_type: this.hasProjectTypeInputTarget ? this.projectTypeInputTarget.value : "screenplay",
        title: this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : "",
        polished_sentence: this.editedConceptText || ""
      }

      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        // Store response data for success step
        this.redirectUrlValue = data.redirect_url
        this.isGmail = data.is_gmail
        this.userEmail = data.email
        this.showSuccess()
      } else {
        this.showEmailError(data.error || "Something went wrong. Please try again.")
        this.setLoading(false)
      }
    } catch (error) {
      console.error("Submit error:", error)
      this.showEmailError("Something went wrong. Please try again.")
      this.setLoading(false)
    }
  }

  showSuccess() {
    this.setLoading(false)

    // Mark wizard as completed to prevent resubmission
    this.markWizardCompleted()

    // Move to success step
    this.currentStepValue = this.totalStepsValue
    this.showStep(this.currentStepValue)

    // Update final concept display
    if (this.hasFinalLoglineTarget) {
      this.finalLoglineTarget.textContent = this.buildConceptText()
    }

    // Display user's email
    if (this.hasUserEmailDisplayTarget && this.userEmail) {
      this.userEmailDisplayTarget.textContent = this.userEmail
    }

    // Show Gmail instant access option if it's a Gmail address
    if (this.hasGmailSectionTarget && this.isGmail) {
      this.gmailSectionTarget.classList.remove("hidden")
    }

    // Set continue link URL
    if (this.hasContinueLinkTarget && this.redirectUrlValue) {
      this.continueLinkTarget.href = this.redirectUrlValue
    }

    // Disable all inputs to prevent modification
    this.disableAllInputs()
  }

  async resendEmail(event) {
    event?.preventDefault()

    if (this.hasResendButtonTarget) {
      const originalText = this.resendButtonTarget.textContent
      this.resendButtonTarget.textContent = "Sending..."
      this.resendButtonTarget.disabled = true

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        // We could add an endpoint for this, but for now just show confirmation
        await new Promise(resolve => setTimeout(resolve, 1000))

        this.resendButtonTarget.textContent = "Sent!"
        setTimeout(() => {
          this.resendButtonTarget.textContent = originalText
          this.resendButtonTarget.disabled = false
        }, 3000)
      } catch (error) {
        console.error("Resend error:", error)
        this.resendButtonTarget.textContent = originalText
        this.resendButtonTarget.disabled = false
      }
    }
  }

  disableAllInputs() {
    const inputs = this.element.querySelectorAll("input, select, textarea, button")
    inputs.forEach(input => {
      if (!input.closest("[data-homepage-wizard-target='continueLink']")) {
        input.disabled = true
      }
    })
  }

  fireConfetti() {
    // Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    })

    // Second burst slightly delayed
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 }
      })
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 }
      })
    }, 250)
  }

  setLoading(loading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
    }
    if (this.hasSubmitTextTarget) {
      this.submitTextTarget.classList.toggle("hidden", loading)
    }
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.toggle("hidden", !loading)
    }
  }

  // Input event handlers for real-time preview
  inputChanged() {
    // Clear edited concept when inputs change so auto-build takes over again
    this.editedConceptText = null
    this.updateConceptPreview()
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      // Submit on email step (step 6), advance on other steps
      if (this.currentStepValue === this.inputStepsValue) {
        this.submit(event)
      } else if (this.currentStepValue < this.inputStepsValue) {
        this.nextStep(event)
      }
    }
    // Only handle Escape for modal version
    if (event.key === "Escape" && this.hasModalTarget) {
      this.closeModal(event)
    }
  }
}
